(() => {
  const ROOT_SELECTOR = '[data-infinite-scroll-root]';
  const LIST_SELECTOR = '[data-infinite-scroll-list]';
  const NEXT_SELECTOR = '[data-infinite-scroll-next]';
  const SENTINEL_SELECTOR = '[data-infinite-scroll-sentinel]';
  const STATUS_SELECTOR = '[data-infinite-scroll-status]';
  const SPINNER_MARKUP =
    '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
  const REINIT_EVENTS = ['theme:product-grid-updated', 'shopify:section:load'];
  const controllers = new WeakMap();

  class InfiniteScrollController {
    constructor(root) {
      this.root = root;
      this.list = root.querySelector(LIST_SELECTOR);
      this.nextLink = root.querySelector(NEXT_SELECTOR);
      this.handleViewportChange = this.handleViewportChange.bind(this);

      if (!this.list || !this.nextLink) return;

      this.isLoading = false;
      this.observer = null;
      this.status = this.getOrCreateStatus();
      this.sentinel = this.getOrCreateSentinel();

      this.observe();
    }

    getOrCreateStatus() {
      const existingStatus = this.root.querySelector(STATUS_SELECTOR);
      if (existingStatus) return existingStatus;

      const status = document.createElement('div');
      status.dataset.infiniteScrollStatus = '';
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('role', 'status');
      status.hidden = true;
      status.style.padding = '1.6rem 0';
      status.style.textAlign = 'center';
      status.innerHTML = `<div class="loading__spinner hidden" style="position: static; display: inline-flex; align-items: center; justify-content: center;">${SPINNER_MARKUP}</div>`;
      this.root.append(status);
      return status;
    }

    getOrCreateSentinel() {
      const existingSentinel = this.root.querySelector(SENTINEL_SELECTOR);
      if (existingSentinel) return existingSentinel;

      const sentinel = document.createElement('div');
      sentinel.dataset.infiniteScrollSentinel = '';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.display = 'block';
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      sentinel.style.marginTop = '1px';
      this.root.append(sentinel);
      return sentinel;
    }

    observe() {
      this.disconnectObserver();

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.loadNextPage();
            }
          });
        },
        {
          rootMargin: '0px 0px 300px 0px',
        }
      );

      this.observer.observe(this.sentinel);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange);
      requestAnimationFrame(() => this.handleViewportChange());
    }

    disconnectObserver() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
    }

    handleViewportChange() {
      if (this.isLoading || !this.nextLink) return;

      const sentinelRect = this.sentinel.getBoundingClientRect();
      const distanceToBottom = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      const sentinelNearViewport = sentinelRect.top - window.innerHeight <= 300;

      if (sentinelNearViewport || distanceToBottom <= 300) {
        this.loadNextPage();
      }
    }

    setLoadingState(isLoading) {
      const spinner = this.status.querySelector('.loading__spinner');

      this.isLoading = isLoading;
      this.root.classList.toggle('is-loading', isLoading);
      this.status.hidden = !isLoading;
      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }
    }

    updateNextLink(nextLink) {
      if (nextLink) {
        this.nextLink.href = nextLink.href;
        return;
      }

      this.nextLink.remove();
      this.disconnectObserver();
      this.status.hidden = true;
    }

    appendItems(items) {
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.append(item));
      this.list.append(fragment);

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this.list);
      }
    }

    async loadNextPage() {
      if (this.isLoading || !this.nextLink) return;

      this.setLoadingState(true);

      try {
        const response = await fetch(this.nextLink.href, {
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`Failed to load next page: ${response.status}`);
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sectionId = this.root.dataset.sectionId;
        const nextRoot =
          (sectionId && doc.querySelector(`${ROOT_SELECTOR}[data-section-id="${sectionId}"]`)) ||
          doc.querySelector(ROOT_SELECTOR);
        const nextList = nextRoot ? nextRoot.querySelector(LIST_SELECTOR) : null;

        if (!nextList) {
          throw new Error('Infinite scroll markup not found in next page response.');
        }

        const newItems = Array.from(nextList.children).filter((item) => item.classList.contains('grid__item'));

        if (newItems.length) {
          this.appendItems(newItems);
        }

        this.updateNextLink(nextRoot.querySelector(NEXT_SELECTOR));
      } catch (error) {
        console.error('Infinite Scroll Error:', error);
      } finally {
        this.setLoadingState(false);
      }
    }

    teardown() {
      this.disconnectObserver();
    }
  }

  const initInfiniteScroll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const existingController = controllers.get(root);
      if (existingController) {
        existingController.teardown();
      }

      const controller = new InfiniteScrollController(root);
      if (controller.list && controller.nextLink) {
        controllers.set(root, controller);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInfiniteScroll);
  } else {
    initInfiniteScroll();
  }

  REINIT_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, initInfiniteScroll);
  });
})();
